interface ParsedCookies {
  getCookie: (name: string) => string | null;
}

interface ParsedCookieEntry {
  raw: string;
  unescaped?: string;
}

interface ParsedCookiesMap {
  [name: string]: ParsedCookieEntry;
}

interface CachedCookieMap {
  [name: string]: string | null;
}

/**
 * Parses a cookie string and returns an interface for reading cookie values.
 */
const parseCookiesString = (cookieString: string): ParsedCookies => {
  const allCookies = document.cookie.length > 0 ? cookieString.split(";") : [];

  const len = allCookies.length;
  const parsedCookiesMap: ParsedCookiesMap = {};

  for (let i = 0; i < len; i++) {
    const cookieParts = allCookies[i].split("=");
    const cookieName = cookieParts[0].trim();
    const cookieValue = cookieParts[1];
    parsedCookiesMap[cookieName] = { raw: cookieValue };
  }

  return {
    getCookie: (cookieName: string): string | null => {
      const entry = parsedCookiesMap[cookieName];
      if (!entry) {
        return null;
      }
      let unescapedValue = entry.unescaped;
      if (unescapedValue === undefined) {
        // We lazily unescape cookies to avoid the cost of unescaping cookies we don't care about (e.g., the AWS creds cookie)
        const rawValue = entry.raw;
        unescapedValue = entry.unescaped = unescape(rawValue.trim());
      }
      return unescapedValue;
    },
  };
};

export const getCookie = (cookieName: string): string | null => {
  const cookiesString = document.cookie;
  const parsedCookies = parseCookiesString(cookiesString);
  return parsedCookies.getCookie(cookieName);
};

let cachedValues: CachedCookieMap = {};

/**
 * This function returns a permanently cached value for a cookie with a given name to avoid reading `document.cookie`.
 * It turns out that just reading `document.cookie` has a performance cost of 1-2ms in Google Chrome (and possibly other browsers).
 * This cost was impacting performance because we have a lot of code that was using data derived from cookies and that code
 * was being called a lot during rendering.
 */
export const getCookieCached = (cookieName: string): string | null => {
  let cachedValue: string | null = cachedValues[cookieName];
  if (cachedValue === undefined) {
    cachedValue = cachedValues[cookieName] = getCookie(cookieName) || null;
  }
  return cachedValue;
};

/**
 * NOTE: Exported for testing
 */
export const _clearCookieCache = () => {
  cachedValues = {};
};
